<script setup lang="ts">
import Icon from '@/components/shared/Icon.vue'
import DropdownShell from '@/components/shared/New/Dropdown/DropdownShell.vue'
import { useProjectedSelf } from '@/composables/useProjectedSelf'
import { getAppVersion } from '@/lib'
import { DropdownMenuItem, DropdownMenuSeparator } from 'radix-vue'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import NotificationSettingsInline from '@/components/modules/Layout/NotificationSettingsInline.vue'
import { useCampaignPolicies } from '@/composables/useCampaignPolicies'

const { data: policies } = useCampaignPolicies()
const router = useRouter()

const { data: projectedSelf } = useProjectedSelf()

const selfInitials = computed(() => {
  return projectedSelf.value?.Record.DN.split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2)
})

const appVersion = getAppVersion()

function handleViewProfile() {
  router.push(`/detail/${projectedSelf?.value?.Record.Id}`)
}

function handleSignOut() {
  window.location.href = '/Account/LogOff'
}

// Match styling with LayoutSavedSearchDropdown.vue
const contentClass =
  'min-w-[20rem] z-40 bg-white rounded-lg border border-gray-300 shadow-[0_10px_40px_10px_rgba(0,0,0,0.08)] overflow-hidden will-change-[transform,opacity] data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade data-[state=closed]:animate-hide dark:bg-gray-900 dark:border-gray-700 dark:shadow-[0_10px_40px_10px_rgba(0,0,0,0.2)]'

const itemClass =
  'w-full flex items-center gap-x-3 py-2.5 px-3 rounded-none text-[14px] text-gray-800 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none dark:text-gray-300 dark:hover:bg-gray-800 dark:focus:bg-gray-800'
</script>

<template>
  <DropdownShell
    v-if="projectedSelf?.Record.DN"
    align="end"
    side="bottom"
    :content-class="contentClass"
  >
    <template #trigger>
      <div
        class="inline-flex gap-x-3 items-center antialiased rounded-full cursor-pointer shrink-0 text-start focus:outline-none"
      >
        <div
          class="shrink-0 size-[38px] rounded-full bg-primary-500 flex items-center justify-center"
        >
          <span class="text-[15px] font-semibold text-white select-none">
            {{ selfInitials }}
          </span>
        </div>
      </div>
    </template>

    <div class="p-3">
      <div class="flex gap-x-3 items-center">
        <div class="flex justify-center items-center rounded-full shrink-0 size-10 bg-primary-500">
          <span class="font-semibold text-white">
            {{ selfInitials }}
          </span>
        </div>
        <div class="min-w-0">
          <div
            class="text-[15px] font-semibold leading-tight text-gray-900 dark:text-gray-100 truncate"
          >
            {{ projectedSelf?.Record.DN }}
          </div>
          <div class="text-[13px] text-gray-500 dark:text-gray-400 truncate">
            {{ projectedSelf?.Record.Email }}
          </div>
          <div class="text-[12px] text-gray-500 dark:text-gray-400 truncate">
            Version: {{ appVersion }}
          </div>
        </div>
      </div>
    </div>

    <template v-if="!policies?.OnlyDefaultNotifications">
      <DropdownMenuSeparator class="h-px bg-gray-200 dark:bg-gray-700" />
      <NotificationSettingsInline />
    </template>
    <DropdownMenuSeparator class="h-px bg-gray-200 dark:bg-gray-700" />
    <div>
      <DropdownMenuItem :class="itemClass" @click="handleViewProfile">
        <Icon variant="eye" class="shrink-0 size-[18px]" :stroke-width="1.75" />
        <span class="cursor-default">View Profile</span>
      </DropdownMenuItem>
    </div>

    <DropdownMenuSeparator class="h-px bg-gray-200 dark:bg-gray-700" />

    <div>
      <DropdownMenuItem :class="itemClass" @click="handleSignOut">
        <Icon variant="log-out" class="shrink-0 size-[18px]" :stroke-width="1.75" />
        <span class="cursor-default">Sign out</span>
      </DropdownMenuItem>
    </div>
  </DropdownShell>
</template>
